import type { CurrentUser } from "@/lib/auth";
import type { AvailableTour, ResolvedTourStep, TourDef, TourText } from "@/features/tours/types";

function resolveText(text: TourText, user: CurrentUser): string {
  return typeof text === "function" ? text(user) : text;
}

function resolveRoute(route: TourDef["steps"][number]["route"], user: CurrentUser): string | null {
  if (!route) return null;
  return typeof route === "function" ? route(user) : route;
}

export function resolveTourSteps(tour: TourDef, user: CurrentUser): ResolvedTourStep[] {
  const steps: ResolvedTourStep[] = [];
  for (const step of tour.steps) {
    if (step.allow && !step.allow(user)) continue;
    steps.push({
      id: step.id,
      target: step.target,
      title: step.title,
      body: resolveText(step.body, user),
      route: resolveRoute(step.route, user),
      advance: step.advance,
      optional: Boolean(step.optional),
    });
  }
  return steps;
}

/**
 * Führungen und Schritte, die zur Rolle passen.
 * `canView` prüft die Seiten-ACL (im Test eine Tabelle, in der App `canViewHref`).
 */
export async function selectToursForUser(
  tours: readonly TourDef[],
  user: CurrentUser | null,
  canView: (aclHref: string) => Promise<boolean> | boolean,
): Promise<AvailableTour[]> {
  if (!user) return [];
  const available: AvailableTour[] = [];
  for (const tour of tours) {
    if (tour.allow && !tour.allow(user)) continue;
    if (!(await canView(tour.aclHref))) continue;
    const steps = resolveTourSteps(tour, user);
    if (steps.length === 0) continue;
    available.push({
      id: tour.id,
      title: tour.title,
      summary: tour.summary,
      steps,
    });
  }
  return available;
}
